import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { aiMatchApi } from '@/api/ai';
import { allocationsApi, resolvedAmounts } from '@/api/allocations';
import { ErrorCodes, isApiError } from '@/api/errors';
import { backendFields, backendNumber } from '@/api/errorDetails';
import { payeesApi, type PayeeCategoryDefault } from '@/api/payees';
import { plannedApi, type Series } from '@/api/planned';
import { reimbursementsApi } from '@/api/reimbursements';
import { reviewApi, reviewItemIsAi, type ReviewItem } from '@/api/review';
import type { Account } from '@/api/accounts';
import {
  lineCounterparty,
  transactionsApi,
  type AllocationLine,
  type OwnerRegisterLine,
} from '@/api/transactions';
import { AiPill } from '@/components/AiPill';
import { ConfirmButton } from '@/components/ConfirmButton';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { DialogGuardBody } from '@/components/DialogGuardBody';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { GuardedDialog } from '@/components/GuardedDialog';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate, formatNumber, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { generateRequestId } from '@/utils/requestId';
import { useSessionOccurrenceId } from '@/features/meetings/sessionContext';
import { NamePayeeForm } from '@/features/payees/NamePayeeForm';
import { useNamePayeeOnLine } from '@/features/payees/useNamePayeeOnLine';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { VirtualAccountRequiredPrompt } from '@/features/virtualAccounts/VirtualAccountRequiredPrompt';
import { useOptionalVirtualAccountsList } from '@/features/virtualAccounts/virtualAccountsListContext';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';
import { isForeignCurrency } from './accountAccess';
import { allocationErrorKey, allocationErrorMessage } from './allocationErrors';
import {
  newRow,
  parsePercentBp,
  partitionParts,
  planIsEmpty,
  planWrites,
  previewAmounts,
  publishedTotal,
  rowsFromParts,
  type AllocateRow,
  type ParsedSlice,
  type PlacedPart,
} from './allocateRows';
import { ALTERNATE_NAME_MAX } from '@/features/payees/PayeeEditDialog';
import { NamedBy } from '@/features/payees/NamingSourcePill';
import { ReimbursementPanel } from './ReimbursementPanel';
import { TransferDestinationPanel } from './TransferDestinationPanel';
import { CounterpartyActions, CounterpartyTag } from './CounterpartyActions';
import {
  DestinationGroupList,
  DestinationRow,
  useDestinationGroups,
  type DestinationGroup,
} from './AllocateDestinations';

export interface AllocateDialogProps {
  account: Account;
  line: OwnerRegisterLine;
  editor: boolean;
  owner: boolean;
  /** Detail-only contexts (the virtual account ledger) suppress writes independently of ownership. */
  readOnly?: boolean;
  onClose: () => void;
  onChanged: (line: OwnerRegisterLine) => void | Promise<void>;
  onNotify: (message: string) => void;
  /** Open with a D1 section expanded (the review queue's "Different expense"). */
  openSection?: 'transfer' | 'reimbursement';
  preferExpenseTransactionId?: number | null;
}

/** The parent's line still says what the dialog last refetched: the refetched copy stays newest. */
function sameLine(a: OwnerRegisterLine, b: OwnerRegisterLine): boolean {
  const parts = (row: OwnerRegisterLine) =>
    row.allocations.map((p) => `${p.id}:${p.amountMinor}:${p.categoryId ?? '-'}`).join(',');
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.remainderMinor === b.remainderMinor &&
    a.transferAccountId === b.transferAccountId &&
    (a.payeeRef?.id ?? null) === (b.payeeRef?.id ?? null) &&
    parts(a) === parts(b)
  );
}

/** The rows' editable content, for the unsaved-work comparison. */
function valuesOf(rows: readonly AllocateRow[]): unknown {
  return rows.map((row) => [
    row.virtualAccountId,
    row.envelopeId,
    row.categoryId,
    row.mode,
    row.value,
  ]);
}

/** A part's origin in words (TMMAPP-0121 row 11); `AI` is the AI pill, an unknown origin is left out. */
const PART_ORIGIN_KEYS: Readonly<Record<string, string>> = {
  USER: 'allocate.origin.USER',
  RULE: 'allocate.origin.RULE',
  PLAN: 'allocate.origin.PLAN',
  SPLIT: 'allocate.origin.SPLIT',
};

/**
 * A part's "origin · actor" caption: the translated origin, or the AI pill for `AI` (TMM-B9), then
 * the served actor; null when the wire carries neither.
 */
function partCaption(part: AllocationLine, t: TFunction): ReactNode {
  const actor = part.actor !== null && part.actor.length > 0 ? part.actor : null;
  if (part.origin === 'AI') {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
      >
        <AiPill />
        {actor !== null && <span>{actor}</span>}
      </Box>
    );
  }
  const key = part.origin === null ? undefined : PART_ORIGIN_KEYS[part.origin];
  const caption = [key === undefined ? null : t(key), actor].filter(Boolean).join(' · ');
  return caption.length > 0 ? caption : null;
}

/** Which request of the plan is in flight, for the server's row-wise answers. */
type InFlight =
  { kind: 'post' | 'split'; rowKeys: string[] } | { kind: 'total' } | { kind: 'name' };

/** A `400` that names the alternate name (`alternateName` or `lines[i].alternateName`). */
function alternateNameRefused(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.code === ErrorCodes.VALIDATION_FAILED &&
    Object.keys(backendFields(error)).some((field) => /(^|\.)alternateName$/.test(field))
  );
}

/**
 * The allocate dialog (TMMAPP-0109; audit 06 § C10 / § Overrides 4; MM-D-113 / 114 / 125 / 128):
 * publishes all or part of a bank line onto a virtual account (its Unassigned) or its envelopes,
 * grouped by virtual account, each row with a category and an amount, a percent or the rest. The
 * totals are previews; the server resolves (`400 resolved[]` shows its amounts inline). Editing
 * uses the split and total routes, never reversal plus re-post; parts the dialog cannot edit are
 * shown read-only and their virtual account is never written. Its D1 sections (Transfer,
 * Reimbursement, a planned-item match) and Void replace the retired `TransactionDrawer`; naming
 * the payee, the void confirm and the subscribe card replace the body in place (DLG-005).
 *
 * TMMAPP-0111 (MM-D-117 / MM-D-126): under the facts box, one "Shows to members as" field for the
 * whole bank line, prefilled with the served `alternateName` (the line's own, else its payee's
 * default). A changed name travels on the post's lines, or — with nothing to post — through the
 * alternate-name `PATCH` (empty clears it); a `400` lands on the field. A named line's payee
 * category default shows as a "Default: {category}" hint on each destination row without one: the
 * server applies it below an explicit choice, so the dialog never sends it.
 */
export function AllocateDialog({
  account,
  line: lineProp,
  editor: requestedEditor,
  owner,
  readOnly = false,
  onClose,
  onChanged,
  onNotify,
  openSection,
  preferExpenseTransactionId = null,
}: AllocateDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const labelInk = tokens.textLabel('surfaceRaised');
  // The line as last refetched here (after a write or a conflict), until the parent hands a newer
  // one — the register reload after `onChanged`.
  const [refetched, setRefetched] = useState<{
    from: OwnerRegisterLine;
    line: OwnerRegisterLine;
  } | null>(null);
  const line = refetched !== null && sameLine(refetched.from, lineProp) ? refetched.line : lineProp;
  const editor = requestedEditor && !readOnly;
  const posted = line.status === 'POSTED';
  const foreign = isForeignCurrency(account);
  const operating = account.operatingCurrency ?? line.currency;
  const incoming = line.operatingAmountMinor > 0;
  const meetingOccurrenceId = useSessionOccurrenceId();
  // No virtual account yet (TMMAPP-0116): the destinations give way to the create prompt.
  const openCreateVirtualAccount = useOptionalVirtualAccountsList()?.openCreate;
  // The destinations: the virtual accounts the caller owns or manages, Unassigned first, then the
  // envelopes; and each one's categories (the shared destinations UI, `AllocateDestinations`).
  const {
    envelopes,
    virtualAccounts,
    status: destinationsStatus,
    error: destinationsError,
    reload: reloadDestinations,
    groups,
    categoriesOf,
    categoryName,
  } = useDestinationGroups();
  const titleId = useId();
  const introId = useId();
  const voidTitleId = useId();
  const subscribeTitleId = useId();
  const nameTitleId = useId();
  const totalsId = useId();

  const envelopeOwner = useCallback(
    (envelopeId: number) => envelopes.find((row) => row.id === envelopeId)?.virtualAccountId,
    [envelopes],
  );
  const minorToText = useCallback(
    (minor: number) => minorToInputText(minor, operating, locale),
    [operating, locale],
  );

  const editableDestination = useCallback(
    (virtualAccountId: number, envelopeId: number | null) =>
      groups.some(
        (group) =>
          group.virtualAccount.id === virtualAccountId &&
          group.destinations.some((d) => d.envelopeId === envelopeId),
      ),
    [groups],
  );
  // The parts the dialog can edit, and the fixed ones it only shows (never written, MM-D-113).
  const { editable: editableParts, fixed: fixedParts } = useMemo(
    () => partitionParts(line, editableDestination, envelopeOwner),
    [line, editableDestination, envelopeOwner],
  );
  const fixedMinor = fixedParts.reduce((sum, placed) => sum + placed.part.amountMinor, 0);
  const lockedVirtualAccountIds = useMemo(
    () =>
      new Set(
        fixedParts.flatMap((placed) =>
          placed.virtualAccountId !== null ? [placed.virtualAccountId] : [],
        ),
      ),
    [fixedParts],
  );
  const partsOf = (virtualAccountId: number): AllocationLine[] =>
    [...editableParts, ...fixedParts]
      .filter((placed) => placed.virtualAccountId === virtualAccountId)
      .map((placed) => placed.part);
  // Parts outside every listed virtual account: unknown, or one the caller does not own.
  const otherParts = fixedParts.filter(
    (placed) =>
      placed.virtualAccountId === null ||
      !groups.some((group) => group.virtualAccount.id === placed.virtualAccountId),
  );

  // One row per destination, plus the line's editable live parts (pre-filled) and any rows added.
  const seedRows = useCallback(
    (parts: readonly PlacedPart[]): AllocateRow[] => {
      const existing = rowsFromParts(parts, minorToText);
      const seeded: AllocateRow[] = [];
      for (const group of groups) {
        for (const destination of group.destinations) {
          const own = existing.filter(
            (row) =>
              row.virtualAccountId === destination.virtualAccountId &&
              row.envelopeId === destination.envelopeId,
          );
          seeded.push(
            ...(own.length > 0
              ? own
              : [newRow(destination.virtualAccountId, destination.envelopeId)]),
          );
        }
      }
      return seeded;
    },
    [groups, minorToText],
  );
  const [rows, setRows] = useState<AllocateRow[]>([]);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [seedValues, setSeedValues] = useState<unknown>(null);
  const seedKey = [
    line.id,
    destinationsStatus,
    groups.map((group) => group.destinations.map((d) => d.envelopeId ?? 'u').join('/')).join('|'),
    line.allocations.map((a) => `${a.id}:${a.amountMinor}:${a.categoryId ?? '-'}`).join(','),
  ].join(':');
  // Seed once the destinations are known; a refetched line (a reimbursement accepted, a reload)
  // or a changed destination list re-seeds so the rows show the resulting parts.
  if (seededFor !== seedKey && destinationsStatus !== 'loading') {
    const seededRows = seedRows(editableParts);
    setSeededFor(seedKey);
    setRows(seededRows);
    setSeedValues(valuesOf(seededRows));
  }
  const seeded = seededFor === seedKey;
  const partOf = (row: AllocateRow): AllocationLine | null =>
    row.allocationId === null
      ? null
      : (line.allocations.find((part) => part.id === row.allocationId) ?? null);
  const contraOf = (row: AllocateRow) => partOf(row)?.contra ?? null;

  const sign = line.operatingAmountMinor < 0 ? -1 : 1;
  const parse = (row: AllocateRow): ParsedSlice => {
    if (row.mode === 'rest') return { kind: 'rest' };
    if (row.value.trim().length === 0) return null;
    if (row.mode === 'percent') {
      const percentBp = parsePercentBp(row.value);
      return percentBp === null ? null : { kind: 'percent', percentBp };
    }
    const magnitude = parseMoneyToMinor(row.value, operating, locale);
    return magnitude === null ? null : { kind: 'amount', amountMinor: sign * Math.abs(magnitude) };
  };
  const parsed = rows.map(parse);
  const invalidTyped = rows.some(
    (row, i) => row.mode !== 'rest' && row.value.trim().length > 0 && parsed[i] === null,
  );
  const restCount = rows.filter((row) => row.mode === 'rest').length;
  const previews = previewAmounts(rows, parsed, line.operatingAmountMinor, fixedMinor);
  // The server's answer wins over the previews once it has spoken (MM-D-125).
  const [resolved, setResolved] = useState<(number | null)[] | null>(null);
  const shown = resolved ?? previews;
  const published = publishedTotal(shown) + fixedMinor;
  const remainder = line.operatingAmountMinor - published;
  const over = Math.abs(published) > Math.abs(line.operatingAmountMinor);
  const plan = planWrites(rows, parsed, previews, editableParts, lockedVirtualAccountIds);
  const publishable = !planIsEmpty(plan);

  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [remainderFromError, setRemainderFromError] = useState<number | null>(null);
  const [focusedRowKey, setFocusedRowKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useRef<string | null>(null);

  // "Shows to members as" (MM-D-117): the served name is the baseline; a refetch re-seeds it.
  const servedName = line.alternateName ?? '';
  const [membersName, setMembersName] = useState(servedName);
  const [membersNameBase, setMembersNameBase] = useState(servedName);
  const [membersNameError, setMembersNameError] = useState<string | null>(null);
  if (membersNameBase !== servedName) {
    setMembersNameBase(servedName);
    setMembersName(servedName);
  }
  const nameChanged = membersName.trim() !== servedName.trim();
  // A name alone saves through the line's live parts; a line with none takes it with its first post.
  const nameSavable = nameChanged && (line.allocations.length > 0 || plan.post.lines.length > 0);

  // The payee's category defaults (MM-D-126): a hint per destination, never sent.
  const payeeId = editor ? (line.payeeRef?.id ?? null) : null;
  const [categoryDefaults, setCategoryDefaults] = useState<{
    payeeId: number;
    rows: PayeeCategoryDefault[];
  } | null>(null);
  useEffect(() => {
    if (payeeId === null) return;
    let cancelled = false;
    void payeesApi
      .get(payeeId)
      .then((detail) => {
        if (!cancelled) setCategoryDefaults({ payeeId, rows: detail.categoryDefaults });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [payeeId]);
  const defaultCategoryOf = (virtualAccountId: number): number | null =>
    categoryDefaults !== null && categoryDefaults.payeeId === payeeId
      ? (categoryDefaults.rows.find((row) => row.virtualAccountId === virtualAccountId)
          ?.categoryId ?? null)
      : null;

  // Unsaved work is the rows' content against what they were seeded with (a re-seed after a
  // refetch is a new baseline); the keys are identity, so a fresh empty row is clean.
  // The transfer section's in-place "New unmanaged account" form reports its typed work too
  // (TMMAPP-0120, TMM-B5).
  const [transferFormDirty, setTransferFormDirty] = useState(false);
  const dirty =
    (seeded && !sameValues(seedValues, valuesOf(rows))) || nameChanged || transferFormDirty;

  // In-place second steps (DLG-005): the void confirm, the subscribe card, naming the payee.
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const voidButtonRef = useRef<HTMLButtonElement>(null);
  const returnVoidFocus = useRef(false);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const subscribeInvoker = useRef<HTMLElement | null>(null);
  const subscribeWasShown = useRef(false);
  const [naming, setNaming] = useState(false);
  const [nameDirty, setNameDirty] = useState(false);
  const nameInvoker = useRef<HTMLElement | null>(null);
  const [clearingName, setClearingName] = useState(false);
  const payeeNaming = useNamePayeeOnLine(account, line, onChanged, onNotify);
  const guard = useDialogDirtyGuard(dirty, onClose, submitting || voiding);

  // `invoker` is the control whose write asked (captured before the write, while it still had
  // focus — a loading button lets it go); focus returns there when the card closes.
  const showSubscribe = useCallback((invoker?: HTMLElement | null) => {
    const active = invoker ?? document.activeElement;
    subscribeInvoker.current = active instanceof HTMLElement ? active : null;
    setVoidOpen(false);
    setSubscribeRequired(true);
  }, []);
  const closeSubscribe = useCallback(() => setSubscribeRequired(false), []);
  const cancelVoid = useCallback(() => {
    if (voiding) return;
    returnVoidFocus.current = true;
    setVoidOpen(false);
  }, [voiding]);
  const openNaming = useCallback((invoker: HTMLElement) => {
    nameInvoker.current = invoker;
    setNaming(true);
  }, []);
  const closeNaming = useCallback(() => setNaming(false), []);
  // The naming step guards its own unsaved work: its Esc / Cancel close the step, not the dialog.
  const nameGuard = useDialogDirtyGuard(
    naming && nameDirty,
    closeNaming,
    payeeNaming.busy,
    0,
    naming,
  );
  useEffect(() => {
    if (!voidOpen && returnVoidFocus.current) {
      returnVoidFocus.current = false;
      voidButtonRef.current?.focus();
    }
  }, [voidOpen]);
  useEffect(() => {
    if (naming || nameInvoker.current === null) return;
    const invoker = nameInvoker.current;
    nameInvoker.current = null;
    if (invoker.isConnected) invoker.focus();
  }, [naming]);
  useEffect(() => {
    if (subscribeRequired) {
      subscribeWasShown.current = true;
      return;
    }
    if (!subscribeWasShown.current) return;
    subscribeWasShown.current = false;
    const invoker = subscribeInvoker.current;
    subscribeInvoker.current = null;
    if (invoker?.isConnected === true) invoker.focus();
    else voidButtonRef.current?.focus();
  }, [subscribeRequired]);

  // The planned-item match proposal (the drawer's D1 panel, moved in).
  const [proposal, setProposal] = useState<ReviewItem | null>(null);
  const [proposalSeries, setProposalSeries] = useState<Series | null>(null);
  useEffect(() => {
    let cancelled = false;
    void reviewApi
      .list('AI_MATCH_PROPOSAL')
      .then((items) => {
        const hit =
          items.find((item) => item.transactionId === line.id && item.proposalId !== null) ?? null;
        if (cancelled) return;
        setProposal(hit);
        if (hit?.seriesId !== null && hit?.seriesId !== undefined) {
          void plannedApi
            .getSeries(hit.seriesId)
            .then((series) => {
              if (!cancelled) setProposalSeries(series);
            })
            .catch(() => undefined);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [line.id]);

  // A confirmed reimbursement shows its section on either side of the pair (the drawer's
  // `reimbConfirmed` branch); re-read when the line's parts change (a confirm, an unlink).
  const [reimbConfirmed, setReimbConfirmed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void reimbursementsApi
      .list('CONFIRMED')
      .then((page) => {
        if (cancelled) return;
        setReimbConfirmed(
          page.items.some(
            (link) => link.creditTransactionId === line.id || link.expenseTransactionId === line.id,
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setReimbConfirmed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [line.id, line.remainderMinor, line.allocations.length]);

  const [open, setOpen] = useState<Record<string, boolean>>(() => ({
    transfer: openSection === 'transfer' || line.transferAccountId !== null,
    reimbursement: openSection === 'reimbursement',
    match: true,
  }));
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  function setRow(key: string, patch: Partial<AllocateRow>) {
    // Editing after a failed submit is a new attempt, and the server's answer no longer applies.
    if (error !== null) idempotencyKey.current = null;
    setResolved(null);
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }
  function addRow(group: DestinationGroup) {
    setResolved(null);
    setRows((prev) => [...prev, newRow(group.virtualAccount.id, null)]);
  }
  function removeRow(key: string) {
    setResolved(null);
    setRows((prev) => prev.filter((row) => row.key !== key));
  }

  async function refetch(): Promise<OwnerRegisterLine> {
    const updated = await transactionsApi.get(account.id, line.id);
    setRefetched({ from: lineProp, line: updated });
    await onChanged(updated);
    return updated;
  }

  async function handlePublish() {
    if (!editor || submitting || invalidTyped || over || restCount > 1) return;
    if (!publishable && !nameSavable) return;
    if (idempotencyKey.current === null) idempotencyKey.current = generateRequestId();
    const key = idempotencyKey.current;
    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSubmitting(true);
    setError(null);
    setRowErrors({});
    setRemainderFromError(null);
    setMembersNameError(null);
    setResolved(null);
    const nextName = membersName.trim();
    // The post carries a new name on every line; otherwise the name has its own write, last.
    const nameOnPost = nameChanged && nextName.length > 0 && plan.post.lines.length > 0;
    // Which request the server is answering, and how many landed before it.
    let inFlight: InFlight = { kind: 'total' };
    let succeeded = 0;
    try {
      // Money leaves first (cleared and reduced groups, in the plan's order), then grows, then
      // the new virtual accounts: I-1 holds at every step.
      for (const cleared of plan.cleared) {
        inFlight = { kind: 'total' };
        await allocationsApi.setVirtualAccountTotal(
          line.id,
          cleared.virtualAccountId,
          { amountMinor: 0 },
          `${key}:total:${cleared.virtualAccountId}`,
        );
        succeeded += 1;
      }
      for (const edit of plan.edits) {
        if (!edit.changed) continue;
        if (edit.totalMinor !== edit.currentMinor) {
          inFlight = { kind: 'total' };
          await allocationsApi.setVirtualAccountTotal(
            line.id,
            edit.virtualAccountId,
            { amountMinor: edit.totalMinor },
            `${key}:total:${edit.virtualAccountId}`,
          );
          succeeded += 1;
        }
        inFlight = { kind: 'split', rowKeys: edit.rowKeys };
        await allocationsApi.split(edit.virtualAccountId, line.id, edit.parts);
        succeeded += 1;
      }
      if (plan.post.lines.length > 0) {
        inFlight = { kind: 'post', rowKeys: plan.post.rowKeys };
        await allocationsApi.post(
          account.id,
          line.id,
          {
            lines: nameOnPost
              ? plan.post.lines.map((postLine) => ({ ...postLine, alternateName: nextName }))
              : plan.post.lines,
            ...(meetingOccurrenceId !== null ? { meetingOccurrenceId } : {}),
          },
          key,
        );
        succeeded += 1;
      }
      if (nameChanged && !nameOnPost) {
        inFlight = { kind: 'name' };
        await allocationsApi.setAlternateName(line.id, nextName.length > 0 ? nextName : null);
        succeeded += 1;
      }
      await refetch();
      idempotencyKey.current = null;
      onNotify(t('accounts.allocated'));
      onClose();
    } catch (err) {
      const rowKeys = inFlight.kind === 'post' || inFlight.kind === 'split' ? inFlight.rowKeys : [];
      const fieldPrefix = inFlight.kind === 'split' ? 'parts' : 'lines';
      const serverResolved = resolvedAmounts(err);
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        showSubscribe(invoker);
      } else if (alternateNameRefused(err)) {
        // A card-, IBAN-, email- or account-number-shaped name (MM-D-117) lands on its field.
        setMembersNameError(t('payee.membersName.invalid'));
        guard.focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.SPLIT_TOTAL_CHANGED) {
        // The line changed elsewhere: the fresh parts re-seed the rows, with the notice.
        setError(t('allocations.errorSplitTotalChanged'));
      } else if (
        isApiError(err) &&
        err.code === ErrorCodes.ALLOCATION_CURRENCY_MISMATCH &&
        rowKeys.length > 0
      ) {
        // A contribution goes in the owner's currency only (MM-D-120): the message sits on the rows
        // bound for a virtual account in another currency (every row of the request if none is).
        const currencyOf = (id: number) =>
          virtualAccounts.find((entry) => entry.id === id)?.currency ?? '';
        const foreign = rowKeys.filter((rowKey) => {
          const currency = currencyOf(
            rows.find((row) => row.key === rowKey)?.virtualAccountId ?? 0,
          );
          return currency.length > 0 && currency !== operating;
        });
        const message = t('allocations.errorCurrencyMismatch');
        setRowErrors(
          Object.fromEntries((foreign.length > 0 ? foreign : rowKeys).map((k) => [k, message])),
        );
        guard.focusErrors();
      } else {
        if (isApiError(err) && err.code === ErrorCodes.IDEMPOTENCY_CONFLICT) {
          idempotencyKey.current = generateRequestId();
        }
        if (serverResolved !== null) {
          // The amounts come back in the failed request's order, onto its rows only.
          const next: (number | null)[] = rows.map(() => null);
          rowKeys.forEach((rowKey, index) => {
            const at = rows.findIndex((row) => row.key === rowKey);
            if (at >= 0) next[at] = serverResolved[index] ?? null;
          });
          setResolved(next);
          setError(t('allocate.resolvedNotice'));
        } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
          const next: Record<string, string> = {};
          const result = applyBackendFields(
            err,
            rowKeys.map((_rowKey, i) => `${fieldPrefix}[${i}].amountMinor`),
            (field, message) => {
              const match = /^(?:lines|parts)\[(\d+)\]/.exec(field);
              const rowKey = match === null ? undefined : rowKeys[Number(match[1])];
              if (rowKey !== undefined) next[rowKey] = message;
            },
            t,
            guard.focusErrors,
          );
          setRowErrors(next);
          setError(validationAlert(result, err, t, t('accounts.errorValidation')));
        } else {
          if (isApiError(err) && err.code === ErrorCodes.ALLOCATION_EXCEEDS_REMAINDER) {
            setRemainderFromError(backendNumber(err, 'remainderMinor'));
          }
          setError(
            allocationErrorMessage(err, t, operating, locale, account.name) ??
              t('accounts.errorGeneric'),
          );
          guard.focusErrors();
        }
      }
      // The amendment: after a response, refetch — and always once part of the plan has landed,
      // so the next plan starts from the line as it now is.
      if (
        serverResolved !== null ||
        succeeded > 0 ||
        (isApiError(err) && err.code === ErrorCodes.SPLIT_TOTAL_CHANGED)
      ) {
        await refetch().catch(() => undefined);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleUseRemainder() {
    if (remainderFromError === null) return;
    const target =
      rows.find((row) => row.key === focusedRowKey && row.mode === 'amount') ??
      rows.find((row) => row.mode === 'amount' && row.value.trim().length > 0) ??
      rows.find((row) => row.mode === 'amount');
    if (target === undefined) return;
    setRow(target.key, { value: minorToText(Math.abs(remainderFromError)) });
  }

  async function handleVoid() {
    if (readOnly || voidReason.trim().length === 0 || voiding) return;
    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setVoiding(true);
    setVoidError(null);
    try {
      await transactionsApi.void(account.id, line.id, { reason: voidReason.trim() });
      await refetch();
      setVoidOpen(false);
      onNotify(t('accounts.voided'));
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        showSubscribe(invoker);
      } else if (isApiError(err) && err.code === ErrorCodes.OCCURRENCE_RESOLVED) {
        // The line moved to another occurrence while it was being voided (TMMAPI-0203): nothing
        // was written, so the dialog shows the line as it now is.
        setVoidError(t('accounts.voidChanged'));
        await refetch().catch(() => undefined);
      } else {
        setVoidError(t(allocationErrorKey(err) ?? 'accounts.errorGeneric'));
      }
    } finally {
      setVoiding(false);
    }
  }

  async function handleClearName() {
    setClearingName(true);
    try {
      const next = await payeesApi.clearOnLine(account.id, line.id);
      setRefetched({ from: lineProp, line: next });
      await onChanged(next);
    } catch {
      onNotify(t('accounts.errorGeneric'));
    } finally {
      setClearingName(false);
    }
  }

  const voidPayee = (line.payeeRef !== null ? line.payeeRef.name : line.payee).trim();
  const nameBusy = payeeNaming.busy || clearingName;

  const counterparty = lineCounterparty(line);
  const facts: { key: string; value: ReactNode }[] = [
    { key: 'allocate.factDate', value: formatCivilDate(line.postedOn, locale) },
    {
      key: 'allocate.factSource',
      value:
        account.last4 !== null && account.last4.length > 0
          ? t('allocate.sourceLast4', { name: account.name, last4: account.last4 })
          : account.name,
    },
    {
      // The description is the counterparty: the resolved name, with "Name this" / Change / Clear
      // beside it for an editor (D1), the bank text beneath a resolved name, the memo beneath both.
      key: 'allocate.factDescription',
      value: (
        <>
          {line.payeeRef !== null ? line.payeeRef.name : line.payee}
          {line.payeeRef !== null && (
            <Typography
              variant="caption"
              component="span"
              sx={{ display: 'block', color: tokens.text2, fontWeight: 400 }}
            >
              {t('payee.bankText', { text: line.payee })}
            </Typography>
          )}
          {/* TMMAPP-0163 item 4: how the name was made, as served. */}
          {line.payeeRef !== null && line.namingSource !== undefined && (
            <Typography
              variant="caption"
              component="span"
              sx={{ display: 'block', color: tokens.text2, fontWeight: 400, mt: 0.5 }}
            >
              <NamedBy
                source={line.namingSource}
                confirmedByPerson={line.confirmedByPerson === true}
              />
            </Typography>
          )}
          {line.memo !== null && line.memo.length > 0 && (
            <Typography
              variant="caption"
              component="span"
              sx={{ display: 'block', color: tokens.text2, fontWeight: 400 }}
            >
              {line.memo}
            </Typography>
          )}
          {editor && posted && (
            <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                size="small"
                disabled={nameBusy}
                onClick={(event) => openNaming(event.currentTarget)}
                sx={{ whiteSpace: 'nowrap', px: 0.5 }}
              >
                {line.payeeRef !== null ? t('payee.change') : t('payee.nameThis')}
              </Button>
              {line.payeeRef !== null && (
                <ConfirmButton
                  size="small"
                  disabled={nameBusy}
                  onConfirm={() => void handleClearName()}
                >
                  {t('payee.clear')}
                </ConfirmButton>
              )}
            </Stack>
          )}
        </>
      ),
    },
    {
      key: 'allocate.factOriginal',
      value: foreign ? (
        <Tooltip
          describeChild
          title={
            line.fxRate !== null
              ? t('accounts.fxRate', {
                  rate: formatNumber(line.fxRate, locale, { maximumFractionDigits: 6 }),
                })
              : ''
          }
        >
          <span>
            <MoneyText minor={line.amountMinor} currency={line.currency} variant="body2" />
          </span>
        </Tooltip>
      ) : (
        '—'
      ),
    },
    {
      key: 'allocate.factBankAmount',
      value: (
        <MoneyText
          minor={line.operatingAmountMinor}
          currency={operating}
          variant="body2"
          tone="register"
        />
      ),
    },
    {
      key: 'allocate.factOrigin',
      value:
        line.importBatchId !== null
          ? t('allocate.originBatch', { id: line.importBatchId })
          : t(`accounts.source.${line.source}`),
    },
  ];

  const confirm = voidOpen
    ? { titleId: voidTitleId, onClose: voiding ? undefined : cancelVoid }
    : subscribeRequired
      ? { titleId: subscribeTitleId, onClose: closeSubscribe }
      : naming
        ? nameGuard.discarding
          ? {
              titleId: nameGuard.titleId,
              consequenceId: nameGuard.consequenceId,
              onClose: nameGuard.requestClose,
            }
          : { titleId: nameTitleId, onClose: nameGuard.requestClose }
        : null;

  const sectionButtonSx = {
    width: '100%',
    justifyContent: 'space-between',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: tokens.text,
    px: 0,
    py: 1,
    borderTop: `1px solid ${tokens.line}`,
    borderRadius: 0,
  } as const;

  /** `adornment` sits after the label (the AI pill on a model's proposal, TMM-B9). */
  const section = (key: string, label: string, body: ReactNode, adornment?: ReactNode) => {
    const id = `${titleId}-${key}`;
    const expanded = open[key] === true;
    return (
      <Box key={key}>
        <Button
          variant="text"
          sx={sectionButtonSx}
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => toggle(key)}
        >
          {adornment === undefined ? (
            <span>{label}</span>
          ) : (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <span>{label}</span>
              {adornment}
            </Box>
          )}
          <Box component="span" aria-hidden sx={{ color: labelInk }}>
            {expanded ? '−' : '+'}
          </Box>
        </Button>
        <Collapse in={expanded} unmountOnExit={false}>
          <Box id={id} sx={{ pb: 1.5 }}>
            {expanded && body}
          </Box>
        </Collapse>
      </Box>
    );
  };

  /** A part the reader sees: its category, the contra tag, the amount, and its origin · actor. */
  const readOnlyPart = (part: AllocationLine, virtualAccountId: number | null) => {
    const caption = partCaption(part, t);
    return (
      <Stack key={part.id} sx={{ alignItems: { sm: 'flex-end' } }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: tokens.text2 }}>
            {categoryName(virtualAccountId, part.categoryId)}
          </Typography>
          {part.contra !== null && (
            <Typography variant="micro" component="span" sx={{ color: tokens.text2 }}>
              {t(`allocate.contra.${part.contra}`)}
            </Typography>
          )}
          <MoneyText minor={part.amountMinor} currency={operating} variant="body2" />
        </Stack>
        {caption !== null && (
          <Typography variant="caption" sx={{ color: tokens.text2 }}>
            {caption}
          </Typography>
        )}
      </Stack>
    );
  };

  const destinationRow = (name: string, balanceMinor: number | null, right: ReactNode) => (
    <DestinationRow name={name} balanceMinor={balanceMinor} currency={operating} right={right} />
  );

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitleId={introId}
      confirm={confirm}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      slotProps={{ paper: { sx: { width: fullScreen ? '100%' : 640, maxWidth: '100%' } } }}
    >
      {voidOpen ? (
        <ConfirmPanel
          titleId={voidTitleId}
          title={
            voidPayee.length > 0
              ? t('accounts.voidNamedTitle', {
                  payee: voidPayee,
                  date: formatCivilDate(line.postedOn, locale),
                })
              : t('accounts.voidDatedTitle', { date: formatCivilDate(line.postedOn, locale) })
          }
          consequence={t('accounts.voidConsequence')}
          confirmLabel={t('accounts.voidConfirm')}
          destructive
          busy={voiding}
          error={voidError}
          confirmDisabled={voidReason.trim().length === 0}
          focusHeading
          onConfirm={() => void handleVoid()}
          onCancel={cancelVoid}
        >
          <TextField
            label={t('accounts.voidReason')}
            value={voidReason}
            onChange={(event) => setVoidReason(event.target.value)}
            disabled={voiding}
            required
            fullWidth
          />
        </ConfirmPanel>
      ) : (
        <>
          {subscribeRequired && (
            <>
              <DialogContent>
                <SubscribeInPlace titleId={subscribeTitleId} focusTitle onClose={onClose} />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeSubscribe}>{t('common.cancel')}</Button>
              </DialogActions>
            </>
          )}
          {!subscribeRequired && naming && (
            <DialogGuardBody guard={nameGuard}>
              <DialogContent>
                <NamePayeeForm
                  bankText={line.payee}
                  titleId={nameTitleId}
                  busy={payeeNaming.busy}
                  focusTitle
                  onCancel={nameGuard.requestClose}
                  onSave={(body) =>
                    void payeeNaming.save(body).then((saved) => {
                      if (saved) closeNaming();
                    })
                  }
                  onDirtyChange={setNameDirty}
                />
              </DialogContent>
            </DialogGuardBody>
          )}
          {/* The body stays mounted under a second step, so focus can return to the control that asked. */}
          <Box sx={{ display: subscribeRequired || naming ? 'none' : 'contents' }}>
            <DialogTitle id={titleId}>{t('allocate.title')}</DialogTitle>
            <DialogSubtitle id={introId}>{t('allocate.intro')}</DialogSubtitle>
            <DialogContent>
              {error !== null && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5 }}
                  action={
                    remainderFromError !== null ? (
                      <Button color="inherit" size="small" onClick={handleUseRemainder}>
                        {t('allocations.useRemainder')}
                      </Button>
                    ) : undefined
                  }
                >
                  {error}
                </Alert>
              )}
              {/* The facts box: 1px line, the control radius, padding 14 16, two columns. */}
              <Box
                component="dl"
                sx={{
                  m: 0,
                  mb: 2,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: `${tokens.radius.control}px`,
                  p: '14px 16px',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 2.5,
                  rowGap: 1.25,
                  fontSize: 13,
                }}
              >
                {counterparty !== null && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <CounterpartyTag counterparty={counterparty} />
                  </Box>
                )}
                {facts.map((fact) => (
                  <Box
                    key={fact.key}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}
                  >
                    <Typography component="dt" variant="body2" sx={{ color: tokens.text2 }}>
                      {t(fact.key)}
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{
                        m: 0,
                        fontWeight: 500,
                        textAlign: 'end',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {fact.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {counterparty !== null && (
                <Box sx={{ mb: 2 }}>
                  <CounterpartyActions
                    account={account}
                    transactionId={line.id}
                    counterparty={counterparty}
                    operatingCurrency={operating}
                    onNotify={onNotify}
                    onChanged={async () => {
                      const fresh = await transactionsApi.get(account.id, line.id);
                      await onChanged(fresh);
                    }}
                  />
                </Box>
              )}

              {editor ? (
                <TextField
                  label={t('payee.membersName.label')}
                  placeholder={line.payeeRef !== null ? line.payeeRef.name : line.payee}
                  value={membersName}
                  onChange={(event) => {
                    setMembersName(event.target.value);
                    setMembersNameError(null);
                  }}
                  error={membersNameError !== null}
                  helperText={membersNameError ?? t('payee.membersName.help')}
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: ALTERNATE_NAME_MAX } }}
                  sx={{ mb: 2 }}
                />
              ) : (
                servedName.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 2, color: tokens.text2 }}>
                    {t('allocate.membersNameRead', { name: servedName })}
                  </Typography>
                )
              )}

              <Typography variant="overline" component="h3" sx={{ display: 'block', mb: 1 }}>
                {t('allocate.destinations')}
              </Typography>
              {destinationsStatus === 'error' && (
                <Box sx={{ mb: 2 }}>
                  <ErrorState
                    variant="section"
                    error={destinationsError}
                    what={t('errors.what.splitDestinations')}
                    onRetry={() => void reloadDestinations()}
                  />
                </Box>
              )}
              {destinationsStatus === 'ready' && groups.length === 0 && otherParts.length === 0 && (
                <Box sx={{ mb: 2 }}>
                  <VirtualAccountRequiredPrompt
                    onCreate={
                      openCreateVirtualAccount !== undefined
                        ? () => {
                            onClose();
                            openCreateVirtualAccount();
                          }
                        : undefined
                    }
                  />
                </Box>
              )}
              {seeded && (
                <DestinationGroupList
                  groups={groups}
                  rows={rows}
                  shown={shown}
                  resolved={resolved}
                  rowErrors={rowErrors}
                  currency={operating}
                  categoriesOf={categoriesOf}
                  contraOf={contraOf}
                  captionOf={(row) => {
                    const part = partOf(row);
                    if (part !== null) return partCaption(part, t);
                    // Only a row with no choice takes the payee's default; Uncategorized never does.
                    const hint =
                      row.categoryId === '' ? defaultCategoryOf(row.virtualAccountId) : null;
                    // Only a category this virtual account offers (loaded, not archived).
                    const known = categoriesOf(row.virtualAccountId).find((c) => c.id === hint);
                    return known === undefined
                      ? null
                      : t('allocate.categoryDefault', { category: known.name });
                  }}
                  editable={(group) =>
                    editor && !lockedVirtualAccountIds.has(group.virtualAccount.id)
                  }
                  groupNotice={(group) =>
                    editor && lockedVirtualAccountIds.has(group.virtualAccount.id) ? (
                      <Typography variant="body2" sx={{ color: tokens.text2, mb: 1 }}>
                        {t('allocate.lockedGroup')}
                      </Typography>
                    ) : null
                  }
                  readOnlyDestination={(group, destination) => (
                    <Stack sx={{ gap: 0.5, alignItems: { sm: 'flex-end' } }}>
                      {partsOf(group.virtualAccount.id)
                        .filter(
                          (part) =>
                            (part.unassigned ? null : part.envelopeId) === destination.envelopeId,
                        )
                        .map((part) => readOnlyPart(part, group.virtualAccount.id))}
                    </Stack>
                  )}
                  groupFooter={(group) =>
                    // Parts of this virtual account outside its listed destinations (archived envelopes).
                    partsOf(group.virtualAccount.id)
                      .filter(
                        (part) =>
                          !group.destinations.some(
                            (d) => d.envelopeId === (part.unassigned ? null : part.envelopeId),
                          ),
                      )
                      .map((part) => (
                        <Box key={part.id} sx={{ borderBottom: `1px solid ${tokens.line}`, py: 1 }}>
                          {destinationRow(
                            part.envelopeName ?? t('accounts.envelopeHidden'),
                            null,
                            <Stack sx={{ gap: 0.5, alignItems: { sm: 'flex-end' } }}>
                              {readOnlyPart(part, group.virtualAccount.id)}
                            </Stack>,
                          )}
                        </Box>
                      ))
                  }
                  onChange={setRow}
                  onRemove={removeRow}
                  onAdd={addRow}
                  onFocusAmount={setFocusedRowKey}
                />
              )}
              {otherParts.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography component="h4" sx={{ fontSize: 13, fontWeight: 500, m: 0, py: 0.5 }}>
                    {t('allocate.otherVirtualAccounts')}
                  </Typography>
                  {otherParts.map(({ part }) => (
                    <Box key={part.id} sx={{ borderBottom: `1px solid ${tokens.line}`, py: 1 }}>
                      {destinationRow(
                        [
                          part.virtualAccountLabel,
                          part.unassigned
                            ? t('virtualAccount.unassigned')
                            : (part.envelopeName ?? t('accounts.envelopeHidden')),
                        ]
                          .filter(Boolean)
                          .join(' · '),
                        null,
                        <Stack sx={{ gap: 0.5, alignItems: { sm: 'flex-end' } }}>
                          {readOnlyPart(part, null)}
                        </Stack>,
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* The totals are previews; the server's resolved[] replaces them (MM-D-125). */}
              <Box id={totalsId} aria-live="polite" sx={{ mt: 1 }}>
                {restCount > 1 && (
                  <Typography variant="body2" sx={{ color: tokens.errorText, mb: 0.5 }}>
                    {t('allocate.oneRest')}
                  </Typography>
                )}
                <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                  <Typography variant="body2" sx={{ color: tokens.text2 }}>
                    {t('allocate.publishedTotal')}
                  </Typography>
                  <MoneyText minor={published} currency={operating} variant="body2" />
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                  <Typography variant="body2" sx={{ color: tokens.text2 }}>
                    {t('allocate.remainderLabel')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: over ? tokens.moneyNegative : tokens.text,
                    }}
                  >
                    <MoneyParts minor={remainder} currency={operating} />
                  </Typography>
                </Stack>
                {over && (
                  <Typography variant="body2" sx={{ color: tokens.errorText }}>
                    {t('accounts.errorExceedsRemainder')}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12, color: labelInk, lineHeight: 1.6, mt: 0.5 }}>
                  {incoming
                    ? t('allocate.hintInflow', { account: account.name })
                    : t('allocate.hintOutflow')}
                </Typography>
              </Box>

              {posted && (
                <Box sx={{ mt: 2 }}>
                  {proposal !== null &&
                    proposal.proposalId !== null &&
                    section(
                      'match',
                      t('review.kind.AI_MATCH_PROPOSAL'),
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          {proposalSeries?.name ?? proposalSeries?.payee ?? ''}
                          {proposal.originalOn !== null
                            ? ` · ${formatCivilDate(proposal.originalOn, locale)}`
                            : ''}
                        </Typography>
                        {editor && (
                          <Stack direction="row" sx={{ gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                void aiMatchApi
                                  .accept(account.id, proposal.proposalId!, generateRequestId())
                                  .then(() => {
                                    setProposal(null);
                                    onNotify(t('review.accept'));
                                  })
                                  .catch(() => onNotify(t('accounts.errorGeneric')));
                              }}
                            >
                              {t('review.accept')}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                void aiMatchApi
                                  .reject(account.id, proposal.proposalId!, generateRequestId())
                                  .then(() => {
                                    setProposal(null);
                                    onNotify(t('review.reject'));
                                  })
                                  .catch(() => onNotify(t('accounts.errorGeneric')));
                              }}
                            >
                              {t('review.reject')}
                            </Button>
                          </Stack>
                        )}
                      </Stack>,
                      reviewItemIsAi(proposal) ? (
                        <AiPill confidence={proposal.confidence} />
                      ) : undefined,
                    )}
                  {(editor || line.transferAccountId !== null) &&
                    section(
                      'transfer',
                      t('transfer.toAccount'),
                      <TransferDestinationPanel
                        account={account}
                        line={line}
                        editor={editor}
                        operatingCurrency={operating}
                        onChanged={onChanged}
                        onNotify={onNotify}
                        onSubscriptionRequired={showSubscribe}
                        onClose={onClose}
                        onDirtyChange={setTransferFormDirty}
                      />,
                    )}
                  {editor &&
                    (incoming || reimbConfirmed) &&
                    section(
                      'reimbursement',
                      t('treatment.reimbursement'),
                      // A line with live allocations is not a reimbursement until they are removed
                      // above (the drawer's refusal); a confirmed pair shows on either side.
                      !reimbConfirmed && line.allocations.length > 0 ? (
                        <Alert severity="warning">
                          {t('transfer.hasAllocations', { count: line.allocations.length })}
                        </Alert>
                      ) : (
                        <ReimbursementPanel
                          // A changed line (a confirm, a reload) remounts the panel so it reads the
                          // confirmed link and its peer afresh, as the drawer's treatment switch did.
                          key={`${line.id}:${line.remainderMinor}:${line.allocations.length}`}
                          account={account}
                          line={line}
                          operatingCurrency={operating}
                          onChanged={onChanged}
                          onNotify={onNotify}
                          onSubscriptionRequired={showSubscribe}
                          preferExpenseTransactionId={preferExpenseTransactionId}
                        />
                      ),
                    )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${tokens.line}` }}>
              {owner && !readOnly && posted && (
                <Button
                  ref={voidButtonRef}
                  variant="text"
                  onClick={() => setVoidOpen(true)}
                  sx={{ marginInlineEnd: 'auto', color: tokens.errorText }}
                >
                  {t('allocate.void')}
                </Button>
              )}
              <Button onClick={guard.requestClose}>
                {editor ? t('common.cancel') : t('common.close')}
              </Button>
              {editor && (
                <SubmitButton
                  onClick={() => void handlePublish()}
                  disabled={
                    invalidTyped ||
                    over ||
                    restCount > 1 ||
                    (!publishable && !nameSavable) ||
                    submitting
                  }
                  loading={submitting}
                >
                  {t('allocate.publish')}
                </SubmitButton>
              )}
            </DialogActions>
          </Box>
        </>
      )}
    </GuardedDialog>
  );
}
